<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  reactive,
  toRefs,
  watch,
} from 'vue'
import { useIntervalFn } from '@vueuse/core'
import { EventType, useEvents } from '@/hooks/useEvents'
import { useDevices } from '@/hooks/useDevices'
import { useGtm } from '@/hooks/useGtm'
import { useEnhancer } from '@/app/enhancer'
import { randomConfig } from '@/constants/state'
import { useCustomization } from '@/hooks/useCustomization'
// import { isProd } from '@/configs/environment'
// import TheLineButton from '@/components/ui/share/TheLineButton.vue'
import switchIconPath from '@/assets/svg/switch-camera-icon.svg?url'
import PlayCircle from '@/assets/svg/play_circle.svg?component'
import OpenGlass from '@/assets/svg/icon_open_glass.svg?component'
import ZoomIn from '@/assets/svg/icon_zoom_in.svg?component'
import ZoomOut from '@/assets/svg/icon_zoom_out.svg?component'

export default defineComponent({
  name: 'ZoomTool',
  components: {
    PlayCircle,
    ZoomIn,
    ZoomOut,
    OpenGlass,
    // TheRandomButton,
  },
  props: {
    isTryOn: {
      type: Boolean,
      default: () => false,
    },
  },
  setup(props) {
    const { isMobile, isIpad, isDesktop, isItReallyMobile } = useDevices()
    const { storeFavorites } = useEnhancer()
    const customization = useCustomization()
    const { pushGtm, pushPixel } = useGtm()
    const eventBus = useEvents()

    const state = reactive({
      zoomLevel: 1,
      pauseRotate: false,
      openGlass: false,
    })

    const trackingRefs = reactive({
      refAutoPlay: null as any,
      refZoomIn: null as any,
      refZoomOut: null as any,
      refSwitching: null as any,
      refShareLine: null as any,
      refCameraSwitch: null as any,
    })

    const zoomMax = computed(() => {
      if (isMobile.value)
        return 9
      if (isIpad.value)
        return 4
      return 2
    })

    const disableZoomIn = computed(() => {
      return state.zoomLevel > zoomMax.value
    })

    const disableZoomOut = computed(() => {
      return state.zoomLevel < 2
    })

    const lineButtonStyle = computed(() => {
      if (!isDesktop.value)
        return { class: 'q-ml-sm' }

      return {
        class: 'absolute-bottom-right',
        style: 'bottom: 22px; right: 22px',
      }
    })

    const switchButtonStyle = computed(() => {
      if (isDesktop.value)
        return { class: 'relative-position text-center' }

      return {
        class: 'absolute-bottom-right',
        style: 'bottom: 130px; right: 22px;',
      }
    })

    const handleAutoRotate = (isUserClick = false) => {
      return new Promise((resolve) => {
        state.pauseRotate = !state.pauseRotate
        eventBus.emit(EventType.ROTATE_AUTO)
        isUserClick && pushGtm(trackingRefs.refAutoPlay?.$el)
        resolve(true)
      })
    }

    const handleZoomIn = () => {
      if (disableZoomIn.value)
        return
      state.zoomLevel++
      eventBus.emit(EventType.ZOOM_IN, state.zoomLevel)
      pushGtm(trackingRefs.refZoomIn?.$el)
    }

    const handleZoomOut = () => {
      if (disableZoomOut.value)
        return
      state.zoomLevel--
      eventBus.emit(EventType.ZOOM_OUT, state.zoomLevel)
      pushGtm(trackingRefs.refZoomOut?.$el)
    }

    const toggleOpenGlass = () => {
      state.openGlass = !state.openGlass
      eventBus.emit(EventType.TOGGLE_OPEN_GLASS, state.openGlass)
      pushGtm(trackingRefs.refSwitching?.$el)
    }

    const shareWithLine = () => {
      eventBus.emit(EventType.SHARE_WITH_LINE)
      pushGtm(trackingRefs.refShareLine?.$el)
      pushPixel(trackingRefs.refShareLine?.$el)
    }

    const handleCameraSwitch = () => {
      eventBus.emit(EventType.SWITCH_CAMERA)
      pushGtm(trackingRefs.refCameraSwitch?.$el)
    }

    const autoRotateIntervalCtx = useIntervalFn(() => {
      handleAutoRotate().then(() => autoRotateIntervalCtx.pause())
    }, randomConfig.timer.autoRotate, { immediate: false })

    watch(() => storeFavorites.isSynced, (isSynced) => {
      isSynced ? autoRotateIntervalCtx.resume() : autoRotateIntervalCtx.pause()
    })

    watch(() => props.isTryOn, () => {
      state.zoomLevel = 1
    })

    onMounted(() => {
      eventBus.on(EventType.ROTATE_END, () => {
        state.pauseRotate = false
        storeFavorites.isSynced && autoRotateIntervalCtx.resume()
      })
      eventBus.on(EventType.ZOOM_RESET, () => {
        if (!props.isTryOn)
          state.zoomLevel = 1
        state.openGlass = false
      })
    })

    onBeforeUnmount(() => {
      eventBus.off(EventType.ROTATE_END)
      eventBus.off(EventType.ZOOM_RESET)
    })

    return {
      ...toRefs(trackingRefs),
      ...toRefs(state),
      disableZoomOut,
      disableZoomIn,
      handleAutoRotate,
      handleZoomIn,
      handleZoomOut,
      toggleOpenGlass,
      switchIconPath,
      handleCameraSwitch,
      isItReallyMobile,
      isIpad,
      // isProd,
      isDemoMode: customization.getCurrentDemoMode,
      lineButtonStyle,
      switchButtonStyle,
      shareWithLine,
    }
  },
})
</script>

<template>
  <section v-show="!isTryOn || isDemoMode" class="q-mb-xs">
    <div v-show="!isTryOn" class="text-center">
      <q-avatar color="grey-2" text-color="dark" size="28px">
        <div class="flex flex-center">
          <span class="text-caption">{{ zoomLevel }}</span>
          <span class="text-small">⨉</span>
        </div>
      </q-avatar>
    </div>
    <div class="flex flex-center q-gutter-md">
      <q-avatar v-show="isTryOn" color="grey-2" text-color="dark" size="28px">
        <div class="flex flex-center">
          <span class="text-caption">{{ zoomLevel }}</span>
          <span class="text-small">⨉</span>
        </div>
      </q-avatar>

      <q-btn
        ref="refSwitching"
        style="width: 54px"
        flat
        :ripple="false"
        glasson-click="view_switching_button"
        round
        @click="toggleOpenGlass"
        v-show="!isTryOn"
      >
        <OpenGlass />
      </q-btn>

      <q-btn
        ref="refAutoPlay"
        style="width: 54px"
        flat
        :ripple="false"
        glasson-click="view_play_button"
        round
        @click="() => handleAutoRotate(true)"
        v-show="!isTryOn"
      >
        <PlayCircle v-show="!pauseRotate" />
        <q-icon v-show="pauseRotate" name="pause_circle_outline" size="lg" />
      </q-btn>

      <q-btn
        ref="refZoomIn"
        flat
        style="width: 54px"
        :ripple="false"
        :disable="disableZoomIn"
        round
        padding="none"
        glasson-click="view_zoom_in_button"
        @click="handleZoomIn"
      >
        <ZoomIn />
      </q-btn>

      <q-btn
        ref="refZoomOut"
        flat
        style="width: 54px"
        :ripple="false"
        :disable="disableZoomOut"
        round
        padding="none"
        glasson-click="view_zoom_out_button"
        @click="handleZoomOut"
      >
        <ZoomOut />
      </q-btn>
    </div>
  </section>
  <div
    class="flex items-start justify-between"
    :style="isIpad ? 'max-width: 480px; margin: 0 auto' : ''"
  >
    <!-- <span>test</span> -->
    <!-- <TheRandomButton
      v-bind="lineButtonStyle"
    /> -->
    <div v-if="isItReallyMobile && isTryOn" v-bind="switchButtonStyle">
      <q-btn
        ref="refCameraSwitch"
        text-color="white"
        color="dark"
        round
        dense
        size="14px"
        glasson-click="menu_camera_switch_button"
        :icon="`img:${switchIconPath}`"
        @click="handleCameraSwitch"
      />
      <span
        class="text-small text-shadow absolute full-width text-white"
        style="bottom: -26px; left: 50%; transform: translate(-50%, 7px)"
        @click="handleCameraSwitch"
      >
        カメラ<br>切替え
      </span>
    </div>
  </div>
</template>
